/**
 * Tooltip Component
 * Displays helpful information on hover
 */

import { useState, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

interface TooltipProps {
  content: string;
  children?: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = "top",
  className = "",
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 256; // max-w-64 = 16rem = 256px
      const padding = 8;

      let top = 0;
      let left = 0;

      switch (side) {
        case "top":
          top = rect.top - padding;
          left = rect.left + rect.width / 2;
          break;
        case "bottom":
          top = rect.bottom + padding;
          left = rect.left + rect.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - padding;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + padding;
          break;
      }

      // Ensure tooltip stays within viewport
      const viewportWidth = window.innerWidth;
      if (left - tooltipWidth / 2 < padding) {
        left = tooltipWidth / 2 + padding;
      } else if (left + tooltipWidth / 2 > viewportWidth - padding) {
        left = viewportWidth - tooltipWidth / 2 - padding;
      }

      setPosition({ top, left });
    }
  }, [isVisible, side]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);
  const handleFocus = () => setIsVisible(true);
  const handleBlur = () => setIsVisible(false);

  const getArrowClasses = () => {
    const base = "absolute w-2 h-2 bg-gray-900 transform rotate-45";
    switch (side) {
      case "top":
        return `${base} -bottom-1 left-1/2 -translate-x-1/2`;
      case "bottom":
        return `${base} -top-1 left-1/2 -translate-x-1/2`;
      case "left":
        return `${base} -right-1 top-1/2 -translate-y-1/2`;
      case "right":
        return `${base} -left-1 top-1/2 -translate-y-1/2`;
    }
  };

  const getTransformClasses = () => {
    switch (side) {
      case "top":
        return "-translate-x-1/2 -translate-y-full";
      case "bottom":
        return "-translate-x-1/2";
      case "left":
        return "-translate-x-full -translate-y-1/2";
      case "right":
        return "-translate-y-1/2";
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        role="button"
        aria-describedby={isVisible ? "tooltip" : undefined}
        className={`inline-flex items-center cursor-help focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded ${className}`}
      >
        {children || (
          <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
        )}
      </span>

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            id="tooltip"
            role="tooltip"
            className={`fixed z-[9999] max-w-64 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg transform ${getTransformClasses()}`}
            style={{ top: position.top, left: position.left }}
          >
            {content}
            <div className={getArrowClasses()} />
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Label with integrated tooltip
 */
interface LabelWithTooltipProps {
  label: string;
  tooltip: string;
  required?: boolean;
  className?: string;
}

export function LabelWithTooltip({
  label,
  tooltip,
  required,
  className = "",
}: LabelWithTooltipProps) {
  return (
    <span className={`flex items-center gap-1.5 ${className}`}>
      {label}
      {required && <span className="text-error-500">*</span>}
      <Tooltip content={tooltip} />
    </span>
  );
}
